import { storeToRefs } from "pinia";
import httpClient from "./httpClient";

import { useAdditionalAttrStore } from "@/stores/AdditionalAttrStore";
import { useLoginStore } from "@/stores/LoginStore";
import { configuratorIdStore } from "@/stores/configuratorIdStore";
import {
  BackDataType,
  ConfiguratorResultResponse,
  ConfiguratorStateResponse,
  CurrentCoding,
  FreeTextResponse,
  Options,
} from "@/stores/types";
import { useOverlayStore } from "@/stores";
import { useConfiguratorStore } from "@/stores/configuratorData";
import i18n from "@/lang";
import { useDataLayerStore } from "@/stores/DataLayerStore";
import { isProductSelected } from "@/utils/responseUtil";

// Login
const login = async () => {
  const loginStore = useLoginStore();
  const attribute = useAdditionalAttrStore();
  const ReqResponse = await httpClient.get("/", {
    params: {
      obj: "login",
      lng: attribute.getLangApi,
      loginname: attribute.getLoginName,
      websessionid: loginStore.generateShortUUID,
      version: attribute.getLoginVersion,
    },
  });
  return ReqResponse.data;
};

// getInitData

const getInitData = async () => {
  const loginStore = useLoginStore();
  const attribute = useAdditionalAttrStore();
  const { getwebsession } = storeToRefs(loginStore);
  const ReqResponse = await httpClient.get("/", {
    params: {
      obj: "getInitData",
      lng: attribute.getLangApi,
      session: getwebsession.value,
    },
  });

  const res = ReqResponse.data;
  useConfiguratorStore().setIsGetResultsCalled(false);

  attribute.setShowAdministratorWidget(
    res.InitData?.AdvancedConfigurator == "Yes" ? true : false
  );

  if (res.Status !== undefined) {
    if (res.Status === "ERROR") {
      const handleEvent = new CustomEvent("errorOccured", {
        bubbles: true,
        detail: {
          event: "getInitData",
          data: res,
        },
      });
      document.dispatchEvent(handleEvent);
    } else {
      if (
        attribute.getDataConfig.modelCodeObj &&
        attribute.getDataConfig.modelCodeObj.length >= 1
      ) {
        const overlay = useOverlayStore();
        overlay.setShowProductPage();
      }
      const handleEvent = new CustomEvent("configuratorLoaded", {
        bubbles: true,
        detail: res.InitData,
      });
      document.dispatchEvent(handleEvent);
    }
  }
  return res;
};

// InitConfigurator

const InitConfigurator = async (): Promise<ConfiguratorResultResponse> => {
  const loginStore = useLoginStore();
  const attribute = useAdditionalAttrStore();
  const idStore = configuratorIdStore();
  const { getwebsession } = storeToRefs(loginStore);

  if (!getwebsession.value) {
    await loginStore.LoginToStore();
  }
  const cis = configuratorIdStore();
  let initModelCode = {};
  // check backData
  if (cis.backData.length) {
    initModelCode = cis.backData;
  }
  if (attribute.dataConfig.currentModelCodeObj?.length) {
    const modelCodeObj = attribute.dataConfig.currentModelCodeObj;
    const resFreeText = await getFreeTexts();
    if (
      typeof resFreeText.FreeTexts !== "undefined" &&
      resFreeText.FreeTexts.length
    ) {
      resFreeText.FreeTexts.forEach((freeText) => {
        modelCodeObj.forEach((modelObject, k) => {
          if (modelObject.TypeObject.CodeType === freeText.CodeTypeId) {
            if (typeof modelCodeObj[k].TypeObject.FreeTexts === "undefined") {
              modelCodeObj[k].TypeObject.FreeTexts = [];
            }
            modelCodeObj[k].TypeObject.FreeTexts?.push({
              Criteria: freeText.CriteriaId,
              Text: freeText.Text,
            });
          }
        });
      });
    }
    initModelCode = modelCodeObj;
    attribute.clearId();
  }
  const ReqResponse = await httpClient.get<ConfiguratorResultResponse>("/", {
    params: {
      obj: "InitConfigurator",
      lng: attribute.getLangApi,
      session: getwebsession.value,
      quantity: attribute.dataConfig.qty,
      allowincomplete: 0,
      hastagnumbers: attribute.getDataConfig.hasTagNumbers ? true : 0,
      autoupdatedrawings: attribute.getDataConfig.autoUpdateDrawings
        ? true
        : false,
      pno: attribute.getDataConfig.planNumber
        ? attribute.getDataConfig.planNumber
        : null,
      modelcodeobj:
        attribute.getDataConfig.modelCodeObj &&
        attribute.getDataConfig.modelCodeObj.length > 0
          ? encodeURIComponent(
              JSON.stringify(attribute.getDataConfig.modelCodeObj)
            )
          : Object.keys(initModelCode).length > 0
          ? encodeURIComponent(JSON.stringify(initModelCode))
          : "",
      itemid: attribute.getDataConfig.itemid
        ? attribute.getDataConfig.itemid
        : cis.backItem
        ? cis.backItem
        : attribute.getIds[0],
    },
  });

  const res = ReqResponse.data;

  if (res.Status !== undefined) {
    if (res.Status === "OK") {
      // set configurator for create build id.
      const currentCoding = res.ConfiguratorResult?.CurrentState?.CurrentCoding;
      cis.initConfiguratorCoding(currentCoding);

      if (res.ConfiguratorResult?.CurrentState.CodeComplete) {
        cis.setPriceSync(true);
      }

      if (Object.keys(cis.backData).length > 1) {
        const mainProduct = currentCoding[0];
        Object.values(cis.backData).forEach(
          (backData: BackDataType, i: number) => {
            if (i > 0) {
              const selectItem = currentCoding.find(
                (item) => item.CodeTypeId == backData.TypeObject.CodeType
              );
              if (selectItem?.Parent === mainProduct.CodeTypeId) {
                useConfiguratorStore().setcertificateSelectorOpen(false);
              }
            }
          }
        );
      }

      const handleEvent = new CustomEvent(
        "configuratorHandleInitConfigurator",
        {
          bubbles: true,
          detail: res.ConfiguratorResult,
        }
      );
      document.dispatchEvent(handleEvent);
    } else {
      const handleEvent = new CustomEvent("errorOccured", {
        bubbles: true,
        detail: {
          event: "InitConfigurator",
          data: res,
        },
      });
      document.dispatchEvent(handleEvent);
      return res;
    }
  }

  const currentState = ReqResponse.data.ConfiguratorResult.CurrentState;
  const currentCoding = currentState.CurrentCoding[0];
  idStore.setRegion(
    ReqResponse.data.ConfiguratorResult.CurrentState.CurrentRegion
  );
  idStore.setInitConfiguratorData(
    currentState.CurrentPlanNo,
    currentState.CurrentType,
    currentCoding.CodeTypeId,
    currentCoding.ContentString
  );
  // clear backData
  cis.clearBackData();
  const handleEvent = new CustomEvent("regionChanged", {
    bubbles: true,
    detail: currentState.CurrentRegion,
  });
  document.dispatchEvent(handleEvent);

  await getInitData();

  return ReqResponse.data;
};

// GetCriteria

const GetCriteria = async (criteriaidx: string, codeType: string) => {
  const loginStore = useLoginStore();
  const attribute = useAdditionalAttrStore();
  const ReqResponse = await httpClient.get("/", {
    params: {
      obj: "GetCriteria",
      lng: attribute.getLangApi,
      codetype: codeType,
      criteriaidx: criteriaidx,
      session: loginStore.getwebsession,
    },
  });

  const res = ReqResponse.data;
  if (res.Status !== undefined) {
    if (res.Status === "ERROR") {
      const handleEvent = new CustomEvent("errorOccured", {
        bubbles: true,
        detail: {
          event: "GetCriteria",
          data: res,
        },
      });
      document.dispatchEvent(handleEvent);
    }
  }
  return ReqResponse.data;
};

const GetStructure = async (CodeTypeId?: string) => {
  const configuratorStore = useConfiguratorStore();
  const loginStore = useLoginStore();

  const ReqResponse = await httpClient.get("/", {
    params: {
      obj: "GetStructure",
      session: loginStore.getwebsession,
      assembly: CodeTypeId
        ? CodeTypeId
        : configuratorStore.getCurrentState?.CurrentCoding.find(
            isProductSelected
          )?.CodeTypeId,
    },
  });

  const res = ReqResponse.data;

  if (res.Status !== undefined) {
    if (res.Status === "ERROR") {
      const handleEvent = new CustomEvent("errorOccured", {
        bubbles: true,
        detail: {
          event: "GetStructure",
          data: res,
        },
      });
      document.dispatchEvent(handleEvent);
    }
  }

  return ReqResponse.data;
};

// Get Type Image

const getTypeImage = async () => {
  const loginStore = useLoginStore();
  const ReqResponse = await httpClient.get("/", {
    params: {
      obj: "getTypeImage",
      session: loginStore.getwebsession,
    },
  });

  const res = ReqResponse.data;
  if (res.Status !== undefined) {
    if (res.Status === "ERROR") {
      const handleEvent = new CustomEvent("errorOccured", {
        bubbles: true,
        detail: {
          event: "getTypeImage",
          data: res,
        },
      });
      document.dispatchEvent(handleEvent);
    }
  }
  return ReqResponse.data;
};

// Get Coding Image

const getCodingImage = async () => {
  const loginStore = useLoginStore();
  const ReqResponse = await httpClient.get("/", {
    params: {
      obj: "getCodingImage",
      session: loginStore.getwebsession,
    },
  });

  const res = ReqResponse.data;
  if (res.Status !== undefined) {
    if (res.Status === "ERROR") {
      const handleEvent = new CustomEvent("errorOccured", {
        bubbles: true,
        detail: {
          event: "getCodingImage",
          data: res,
        },
      });
      document.dispatchEvent(handleEvent);
    }
  }
  return ReqResponse.data;
};

// Get Free Texts

const getFreeTexts = async (): Promise<FreeTextResponse> => {
  const loginStore = useLoginStore();
  const ReqResponse = await httpClient.get<FreeTextResponse>("/", {
    params: {
      obj: "getFreeTexts",
      session: loginStore.getwebsession,
    },
  });

  const res = ReqResponse.data;
  if (res?.Status === "ERROR") {
    const handleEvent = new CustomEvent("errorOccured", {
      bubbles: true,
      detail: {
        event: "getFreeTexts",
        data: res,
      },
    });
    document.dispatchEvent(handleEvent);
  }
  return res;
};

//  SetCriteria

const SetCriteria = async (Options: Options, codeType: string) => {
  const loginStore = useLoginStore();
  const ReqResponse = await httpClient.get("/", {
    params: {
      obj: "SetCriteria",
      session: loginStore.getwebsession,
      codetype: codeType,
      criteriaidx: Options.CriteriaIdx,
      value: Options.OptionId,
      confirm: Options.confirm,
      textvalue: Options.TextOptionValue,
      variablevalue: Options.VariableValue,
      fullresult: true,
    },
  });

  useConfiguratorStore().setlastSelectedOption(Options);
  useConfiguratorStore().setAllowallowOptionalModels(true);

  useConfiguratorStore().setlastSelectedcodeType(
    typeof codeType == "string"
      ? codeType
      : ReqResponse.data.CurrentState.CurrentType
  );
  let showModel = false;
  ReqResponse.data.CurrentState?.CurrentCoding.map((code: Options) =>
    code.Originators?.map((originator) =>
      originator.CriteriaId == Options.CriteriaId ? (showModel = true) : ""
    )
  );

  const assemblies = ReqResponse.data.CurrentState?.CurrentCoding.filter(
    (item: CurrentCoding, index: number) =>
      index !== 0 &&
      item.Parent === "" &&
      item.CodeTypeId != "LABEL" &&
      item.Optional === "Yes" &&
      !item?.Originators?.some(
        (org) =>
          org.CodeTypeId !== useConfiguratorStore().lastSelectedcodeType ||
          org.CriteriaId !==
            useConfiguratorStore().getLastselectedCriteriaOption?.CriteriaId ||
          org.OptionId !==
            useConfiguratorStore().getLastselectedCriteriaOption?.OptionId
      )
  );

  if (showModel && assemblies.length) {
    useConfiguratorStore().setlastSelectedcodeTypeOpenWith(
      Options.CriteriaIdx + Options.OptionId
    );

    useConfiguratorStore().setAssemblySelectorOpen(true);
  }

  useConfiguratorStore().getlastSelectedcodeTypeOpenWith.map((code) => {
    if (code === Options.CriteriaIdx + Options.OptionId) {
      useConfiguratorStore().setAssemblySelectorOpen(true);
    }
  });

  useConfiguratorStore().setIsGetResultsCalled(false);

  const res = ReqResponse.data;
  if (res.Status !== undefined) {
    let handleEvent;
    if (res.Status === "OK") {
      handleEvent = new CustomEvent("optionSelected", {
        bubbles: true,
        detail: res,
      });
    } else {
      handleEvent = new CustomEvent("errorOccured", {
        bubbles: true,
        detail: {
          event: "SetCriteria",
          data: res.CurrentState,
        },
      });
    }
    document.dispatchEvent(handleEvent);
  }
  useDataLayerStore().dataLayerConfiguratorSetCriteria();
  return ReqResponse.data;
};

const ChangeAssembly = async (
  CodeTypeId: string
): Promise<ConfiguratorResultResponse> => {
  const loginStore = useLoginStore();
  useConfiguratorStore().setIsGetResultsCalled(false);
  const ReqResponse = await httpClient.get<ConfiguratorResultResponse>("/", {
    params: {
      obj: "ChangeAssembly",
      newassembly: CodeTypeId,
      session: loginStore.getwebsession,
    },
  });
  const response = await GetStructure(CodeTypeId);
  useConfiguratorStore().setStructureValue(response.Structure);
  return ReqResponse.data;
};

const resetAssembly = async (
  CodeTypeId: string
): Promise<ConfiguratorStateResponse> => {
  const loginStore = useLoginStore();
  useConfiguratorStore().setIsGetResultsCalled(false);
  const response = await httpClient.get<ConfiguratorStateResponse>("/", {
    params: {
      obj: "ResetAssembly",
      assembly: CodeTypeId,
      fullresult: true,
      session: loginStore.getwebsession,
    },
  });
  return response.data;
};

const GetTechnicalEvaluation = async () => {
  const configuratorStore = useConfiguratorStore();
  if (
    configuratorStore?.getRequireTechnicalEvaluation === false &&
    configuratorStore.getCurrentState?.CodeComplete == false
  ) {
    return null;
  }
  const loginStore = useLoginStore();
  const attribute = useAdditionalAttrStore();
  const signalKey = "getTechnicalEvaluation";
  const ReqResponse = await httpClient.get("/", {
    params: {
      obj: "getTechnicalEvaluation",
      session: loginStore.getwebsession,
      lng: attribute.getLangApi,
    },
    signalKey,
  });
  return ReqResponse.data;
};

const GetResults = async () => {
  const loginStore = useLoginStore();
  const attribute = useAdditionalAttrStore();
  const signalKey = "getResults";
  const ReqResponse = await httpClient.get("/", {
    params: {
      obj: "getResults",
      session: loginStore.getwebsession,
      lng: attribute.getLangApi,
      suppresslogout: true,
    },
    signalKey,
  });
  await useDataLayerStore().dataLayerSelectItem();
  await useDataLayerStore().dataLayerconfiguratorEnd();
  return ReqResponse.data;
};

const SetParameter = async (
  parametername: string,
  parametervalue: string | number
) => {
  const loginStore = useLoginStore();
  useConfiguratorStore().setIsGetResultsCalled(false);
  if (loginStore.getwebsession !== "") {
    const attribute = useAdditionalAttrStore();
    const ReqResponse = await httpClient.get("/", {
      params: {
        obj: "setParameter",
        session: loginStore.getwebsession,
        lng: attribute.getLangApi,
        parametername: parametername,
        parametervalue: parametervalue,
      },
    });
    const res = ReqResponse.data;
    if (res.Status !== undefined) {
      if (res.Status === "ERROR") {
        const handleEvent = new CustomEvent("errorOccured", {
          bubbles: true,
          detail: {
            event: "setParameter",
            data: res,
          },
        });
        document.dispatchEvent(handleEvent);
      }
    }
    return ReqResponse.data;
  }
  return null;
};

const GetDatasheet = async (docformat: string) => {
  const loginStore = useLoginStore();
  const attribute = useAdditionalAttrStore();
  const ReqResponse = await httpClient.get("/", {
    params: {
      obj: "getItemBasedDatasheet",
      session: loginStore.getwebsession,
      lng: attribute.getLangApi,
      docformat: docformat,
    },
  });
  const res = ReqResponse.data;
  if (res.Status !== undefined) {
    if (res.Status === "ERROR") {
      const handleEvent = new CustomEvent("errorOccured", {
        bubbles: true,
        detail: {
          event: "getItemBasedDatasheet",
          data: res,
        },
      });
      document.dispatchEvent(handleEvent);
    }
  }
  return ReqResponse.data;
};

const GetHelpTexts = async (assembly: string) => {
  const loginStore = useLoginStore();
  const ReqResponse = await httpClient.get("/", {
    params: {
      obj: "getHelpTexts",
      session: loginStore.getwebsession,
      assembly: assembly,
    },
  });
  const res = ReqResponse.data;
  if (res.Status !== undefined) {
    if (res.Status === "ERROR") {
      const handleEvent = new CustomEvent("errorOccured", {
        bubbles: true,
        detail: {
          event: "getHelpTexts",
          data: res,
        },
      });
      document.dispatchEvent(handleEvent);
    }
  }
  return ReqResponse.data;
};

const Logout = async () => {
  const loginStore = useLoginStore();
  const ReqResponse = await httpClient.get("/", {
    params: {
      obj: "logout",
      session: loginStore.getwebsession,
    },
  });
  const res = ReqResponse.data;
  if (res.Status !== undefined) {
    if (res.Status === "ERROR") {
      const handleEvent = new CustomEvent("errorOccured", {
        bubbles: true,
        detail: {
          event: "logout",
          data: res,
        },
      });
      document.dispatchEvent(handleEvent);
    }
  }

  return ReqResponse.data;
};

const GetExternalItemid = async (
  itemId: string,
  externalItemNoRequestUrl: string
) => {
  const url = externalItemNoRequestUrl.replace("{itemNo}", itemId);

  const ReqResponse = await httpClient
    .get(url, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .catch((error) => {
      console.log(error);
      return {
        data: {
          itemId: null,
        },
      };
    });
  return ReqResponse.data;
};

const SetLanguageFromFile = async (newLang: string) => {
  const attribute = useAdditionalAttrStore();
  const filepath = attribute.getDataConfig.assetBaseUrl + "/languages/";
  const langPath = filepath ?? "";
  let path = "";
  const patern = /^https?:\/\//i;
  if (patern.test(langPath)) {
    path = langPath;
  } else {
    path = window.location.origin + "/" + langPath;
  }

  const ReqResponse = await httpClient
    .get(path + newLang + ".json", {
      withCredentials: true,
    })
    .catch((error) => {
      console.log(error);
      return {
        data: {},
      };
    });

  i18n.global.setLocaleMessage(newLang, ReqResponse.data);
  i18n.global.locale = newLang;
};

export {
  login,
  getInitData,
  InitConfigurator,
  GetCriteria,
  SetCriteria,
  getCodingImage,
  getTypeImage,
  getFreeTexts,
  ChangeAssembly,
  resetAssembly,
  GetResults,
  SetParameter,
  GetDatasheet,
  GetHelpTexts,
  Logout,
  GetExternalItemid,
  SetLanguageFromFile,
  GetStructure,
  GetTechnicalEvaluation,
};
